/**
 * XPEN-3631: the choices the supplier form offers for a payment term.
 *
 * A term is stored on a supplier as its number of days — that is what a pay
 * date is computed from — while each firm names its terms with codes of its
 * own: `NET30` at one, `200` at another, `2NET10` at a third. The code is
 * meaningless outside the firm, so the day count is the value and the firm's
 * own wording is the label.
 *
 * A firm with no terms synced keeps the fixed choices the form has always
 * offered, so no tenant loses its picker.
 */

import Utils from './utils';

export function paymentTermLabel(term, data) {
    const described = (term?.description ?? '').trim();
    if (described) return described;
    const days = term?.net_days;
    if (days === 0) return data?.t?.read('cash_on_delivery') ?? 'Cash on delivery';
    return `Net ${days} ${data?.t?.read('day') ?? 'day'}s`;
}

export function defaultPaymentTerm(terms) {
    // XPEN-3635: the term the firm's accounting system marks as its own. Only
    // an explicit flag counts — a firm that names no default gets no guess,
    // and the field stays empty as it was before.
    //
    // A firm marking two different day counts as default is not a shape the
    // accounting system produces, and no tenant does it; were it to happen,
    // the first wins, which after the API's ordering is the shortest term.
    const marked = (terms ?? []).find((term) => term?.is_default === true && Number.isInteger(term?.net_days));
    return marked ? marked.net_days : null;
}

export function buildPaymentTermsOptions(terms, data, currentValue = null) {
    const usable = (terms ?? []).filter((term) => Number.isInteger(term?.net_days));
    // Not an error when there are none: a firm whose accounting system has no
    // terms synced, and every tenant before this endpoint existed.
    const options = usable.length === 0
        ? Utils.paymentTermsOptions(data)
        : usable.map((term) => ({ label: paymentTermLabel(term, data), value: term.net_days }));

    // A supplier can hold a term the firm has since retired. Without an option
    // carrying it the control renders blank, which reads as missing data —
    // the very complaint this ticket started from. Show what is stored.
    if (Number.isInteger(currentValue) && !options.some((o) => o.value === currentValue)) {
        return [...options, { label: paymentTermLabel({ net_days: currentValue }, data), value: currentValue }];
    }
    return options;
}
